import { z } from "zod";
import { shoppingOrderEvidence } from "./shopping.ts";

export const catalogSearchInput = z.object({
  query: z.string().trim().min(1).max(500),
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .default("US"),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .default("USD"),
  maxPrice: z.number().int().positive().optional(),
  cursor: z.string().max(4096).optional(),
});
export type CatalogSearchInput = z.infer<typeof catalogSearchInput>;
export const catalogProductInput = z.object({
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  id: z.string().min(1).max(2048),
  selected: z
    .array(z.object({ name: z.string().max(100), label: z.string().max(255) }))
    .max(20)
    .optional(),
});
export const catalogVariant = z.object({
  id: z.string(),
  title: z.string(),
  url: z.string().url(),
  checkoutUrl: z.string().url().optional(),
  amountMinor: z.number().int().nonnegative(),
  currency: z.string(),
  available: z.boolean(),
  merchant: z.string(),
  merchantDomain: z.string(),
  checkoutMode: z.enum(["assisted", "merchant"]).optional(),
  images: z.array(z.object({ url: z.string().url(), alt: z.string() })),
  options: z.array(z.object({ name: z.string(), label: z.string() })),
  rating: z.object({ value: z.number(), count: z.number().int().nonnegative() }).optional(),
});
export type CatalogVariant = z.infer<typeof catalogVariant>;
export const catalogProduct = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  images: catalogVariant.shape.images,
  variants: z.array(catalogVariant),
  options: z.array(z.object({ name: z.string(), values: z.array(z.string()) })),
});
export type CatalogProduct = z.infer<typeof catalogProduct>;
export const catalogResults = z.object({
  products: z.array(catalogProduct),
  cursor: z.string().optional(),
  hasMore: z.boolean(),
});
export type CatalogResults = z.infer<typeof catalogResults>;
export const catalogSelection = z.object({
  country: z
    .string()
    .regex(/^[A-Z]{2}$/)
    .optional(),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/)
    .optional(),
  productId: z.string().min(1).max(2048),
  variantId: z.string().min(1).max(2048),
  quantity: z.number().int().min(1).max(100).default(1),
});
export type CatalogSelection = z.infer<typeof catalogSelection>;
export const catalogRecommendationInput = z.object({
  selection: catalogSelection,
  reason: z.string().trim().min(20).max(1000),
  tradeoff: z.string().trim().min(10).max(500),
});
export const shoppingRecommendationsInput = z.object({
  search: catalogSearchInput,
  summary: z.string().trim().min(20).max(1000),
  options: z
    .array(
      catalogRecommendationInput.extend({
        sourceUrls: z
          .array(z.string().url().startsWith("https://").max(2048))
          .max(3)
          .optional()
          .describe(
            "Additional public merchant or manufacturer pages you read to verify this exact variant; the server independently fetches them",
          ),
      }),
    )
    .min(1)
    .max(2),
});
export type ShoppingRecommendationsInput = z.infer<typeof shoppingRecommendationsInput>;
export const savedShoppingRecommendations = z.object({
  summary: shoppingRecommendationsInput.shape.summary,
  options: z
    .array(
      catalogRecommendationInput.extend({
        fingerprint: z.string(),
        requestId: z.string().uuid(),
      }),
    )
    .min(1)
    .max(2),
});
export const shoppingRecommendations = z.object({
  summary: z.string(),
  options: z.array(catalogRecommendationInput.extend({ changed: z.boolean() })),
});
export type ShoppingRecommendations = z.infer<typeof shoppingRecommendations>;
export type ShoppingResultsResponse = {
  results: CatalogResults;
  selection?: CatalogSelection;
  recommendations?: ShoppingRecommendations;
};
export const shoppingCardSpec = z.discriminatedUnion("kind", [
  z.object({
    kind: z.literal("results"),
    artifactId: z.string(),
    threadId: z.number().int(),
    search: catalogSearchInput,
    recommendationSummary: z.string().optional(),
  }),
  z.object({ kind: z.literal("order"), orderId: z.string().uuid(), threadId: z.number().int() }),
]);
export type ShoppingCardSpec = z.infer<typeof shoppingCardSpec>;
export const shoppingOrderStatus = z.enum([
  "selected",
  "preparing",
  "awaitingApproval",
  "processing",
  "confirmed",
  "cancelled",
  "needsAttention",
  "outcomeUnknown",
]);
export type ShoppingOrderStatus = z.infer<typeof shoppingOrderStatus>;
export const shoppingShipment = z.object({
  id: z.string(),
  stage: z.enum(["labelCreated", "shipped", "outForDelivery", "delivered", "exception", "unknown"]),
  carrier: z.string().optional(),
  trackingUrl: z.string().url().optional(),
  estimatedDelivery: z.string().optional(),
  deliveredAt: z.string().datetime().optional(),
});
export type ShoppingShipment = z.infer<typeof shoppingShipment>;
export const catalogOrderSummary = z.object({
  id: z.string().uuid(),
  threadId: z.number().int(),
  status: shoppingOrderStatus,
  title: z.string(),
  merchant: z.string(),
  quantity: z.number().int(),
  productUrl: z.string().url(),
  paymentAttemptId: z.string().uuid().optional(),
  paymentArtifactId: z.string().optional(),
  order: shoppingOrderEvidence.omit({ url: true }).optional(),
  shipments: z.array(shoppingShipment),
  tracking: z.boolean(),
  checkedAt: z.string().datetime().optional(),
});
export type CatalogOrderSummary = z.infer<typeof catalogOrderSummary>;
export const createCatalogOrderReq = catalogSelection.extend({
  artifactId: z.string().min(1).max(255),
  requestId: z.string().uuid(),
});
export const shoppingRequestContext = z.object({ threadId: z.number().int().positive() });
export const prepareRecommendationInput = z.object({
  artifactId: z.string().min(1).max(255),
  option: z.number().int().min(1).max(2),
});
export type PrepareRecommendationInput = z.infer<typeof prepareRecommendationInput>;
export type ShoppingPreparation =
  | { kind: "order"; order: CatalogOrderSummary }
  | { kind: "connectLink"; artifactId: string }
  | { kind: "merchant"; url: string };
export const shoppingTrackingReq = z.object({ enabled: z.boolean() });
export const shoppingOrderParams = z.object({ id: z.string().uuid() });
export const shoppingCardParams = z.object({ id: z.string().min(1).max(255) });
export const shoppingSearchReq = catalogSearchInput.extend({
  threadId: z.number().int().positive(),
});
export const shoppingWalletStatus = z.object({
  enabled: z.boolean(),
  status: z.enum(["not_connected", "connecting", "connected", "needs_attention"]),
});
export type ShoppingWalletStatus = z.infer<typeof shoppingWalletStatus>;
